import { useState, useMemo, useEffect, useRef } from 'react';

interface UsePaginationProps<T> {
  data: T[];
  itemsPerPage: number;
  // Number of siblings to show on each side of the current page
  siblingCount?: number;
  totalCount?: number;
  filter?: string | null;
}

interface PaginationResult<T> {
  currentPage: number;
  totalPages: number;
  paginationRange: (number | string)[];
  currentPageData: T[];
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToPage: (page: number) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const usePagination = <T>({
  data,
  itemsPerPage,
  siblingCount = 1,
  totalCount,
  filter,
}: UsePaginationProps<T>): PaginationResult<T> => {
  const _filter = useRef<string | null>();
  const [currentPage, setCurrentPage] = useState(1);
  const totalItems = totalCount ? totalCount : data.length;
  // important: currently BE supports max 10000 items lookup from opensearch so we are limiting our pagination to that number
  const totalPages =
    totalItems > 10000 ? Math.ceil(10000 / itemsPerPage) : Math.ceil(totalItems / itemsPerPage);

  // Helper function to create an array of numbers from start to end
  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
  };

  // Pagination range is the range of page numbers that will be displayed in the pagination component.
  const paginationRange = useMemo(() => {
    // +5 because we always want to show first, last, current and possible dots on both sides
    const totalPageNumbers = siblingCount + 5;

    if (totalPages > totalPageNumbers) {
      const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
      const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

      const showLeftDots = leftSiblingIndex > 2;
      const showRightDots = rightSiblingIndex < totalPages - 2;

      const firstPageIndex = 1;
      const lastPageIndex = totalPages;

      if (!showLeftDots && showRightDots) {
        const leftItemCount = 3 + 2 * siblingCount;
        const leftRange = range(1, leftItemCount);

        return [...leftRange, '...', totalPages];
      }

      if (showLeftDots && !showRightDots) {
        const rightItemCount = 3 + 2 * siblingCount;
        const rightRange = range(totalPages - rightItemCount + 1, totalPages);
        return [firstPageIndex, '...', ...rightRange];
      }

      if (showLeftDots && showRightDots) {
        const middleRange = range(leftSiblingIndex, rightSiblingIndex);
        return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
      }
    }

    return range(1, totalPages);
  }, [totalPages, currentPage, siblingCount]);

  const currentPageData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return data.slice(start, end);
  }, [currentPage, data, itemsPerPage]);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  // If the query filter associated with a component that uses pagination changes,
  // we want to reset the pagination to the first page.
  useEffect(() => {
    if (_filter.current !== filter) {
      setCurrentPage(1);
      _filter.current = filter;
    }
  }, [filter]);

  return {
    currentPage,
    totalPages,
    paginationRange,
    currentPageData,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
};

export default usePagination;
